<script setup>
import { Head, Link } from '@inertiajs/vue3';
import FrontLayout from '@/Layouts/FrontLayout.vue';
import PageHero from '@/Components/Front/PageHero.vue';
import SocialShare from '@/Components/Front/SocialShare.vue';

const props = defineProps({
    setting: Object,
    service: Object, // The Gallery Item
    latestServices: Array,
    archives: Array, // Added archives prop
    title: String,
    meta_description: String,
    canonical_url: String,
    og_image: String,
});

const getImageUrl = (image) => {
    if (image?.image_url) return image.image_url;
    if (image?.image_path) return `/storage/gallery/${image.image_path}`;
    return '/front/img/default-image.jpg';
};

const formatDate = (dateString, mode = 'long') => {
    if (!dateString) return '';
    const options = mode === 'short' 
        ? { day: 'numeric', month: 'short', year: 'numeric' }
        : { day: 'numeric', month: 'long', year: 'numeric' };
    return new Intl.DateTimeFormat('id-ID', options).format(new Date(dateString));
};

const getMonthName = (monthNumber) => {
    const date = new Date();
    date.setMonth(monthNumber - 1);
    return new Intl.DateTimeFormat('id-ID', { month: 'long' }).format(date);
};
</script>

<template>
    <FrontLayout 
        :setting="setting"
        :title="title"
        :metaDescription="meta_description"
        :ogImage="og_image"
    >
        <Head>
            <link v-if="canonical_url" rel="canonical" :href="canonical_url" />
            <meta property="og:type" content="article" />
            <meta property="og:site_name" content="Gotransport Banyuwangi" />
        </Head>

        <!-- Use PageHero matching News/Detail -->
        <PageHero 
            :title="service.title"
            :subtitle="'Dokumentasi Layanan Sewa Mobil Banyuwangi'"
            :breadcrumbs="[
                { label: 'Beranda', url: '/' },
                { label: 'Layanan', url: '/layanan' },
                { label: 'Detail' }
            ]"
            waveClass="text-gray-50 dark:text-gray-900"
        />

        <section class="py-12 bg-gray-50 dark:bg-gray-900 min-h-[600px]">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    
                    <!-- Main Content (lg:col-span-2) -->
                    <div class="lg:col-span-2">
                        <article class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <!-- Featured Image -->
                            <div class="w-full">
                                <img :src="getImageUrl(service)" :alt="service.title" class="w-full h-auto">
                            </div>

                            <div class="p-4">
                                <!-- Meta header -->
                                <div class="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-100 dark:border-gray-700">
                                    <span class="flex items-center gap-2">
                                        <i class="fa fa-calendar text-orange-500"></i> {{ formatDate(service.date_taken || service.created_at) }}
                                    </span>
                                    <span class="flex items-center gap-2" v-if="service.category">
                                        <i class="fa fa-folder text-orange-500"></i> {{ service.category }}
                                    </span>
                                </div>

                                <!-- Content -->
                                 <div class="prose dark:prose-invert max-w-none text-sm prose-orange prose-img:rounded-xl prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white prose-a:text-blue-600 hover:prose-a:text-blue-700 transition-colors prose-p:my-1.5 prose-headings:my-3 prose-li:my-0">
                                     <p class="whitespace-pre-line leading-relaxed">{{ service.description }}</p>
                                     <div v-if="!service.description" class="text-gray-500 dark:text-gray-400 italic">
                                         Tidak ada deskripsi tambahan.
                                     </div>
                                </div>

                                <!-- Share -->
                                <div class="mt-8 pt-8 border-t border-gray-100 dark:border-gray-700">
                                    <SocialShare :url="canonical_url" :title="service.title" />
                                </div>
                            </div>
                        </article>

                        <!-- Related Services (Latest) - Bottom -->
                        <div class="mt-12" v-if="latestServices && latestServices.length > 0">
                            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                                <i class="fa fa-th-large text-orange-600"></i> Layanan Lainnya
                            </h3>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div v-for="item in latestServices" :key="item.id" class="group bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all">
                                    <div class="aspect-[4/3] overflow-hidden">
                                        <Link :href="`/layanan/${item.slug}`">
                                            <img :src="getImageUrl(item)" :alt="item.title" class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500">
                                        </Link>
                                    </div>
                                    <div class="p-4">
                                        <div class="text-xs text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-2"><i class="fa fa-calendar"></i> {{ formatDate(item.date_taken || item.created_at, 'short') }}</div>
                                        <h4 class="font-bold text-gray-900 dark:text-white line-clamp-2 leading-snug group-hover:text-orange-600 transition-colors">
                                            <Link :href="`/layanan/${item.slug}`">{{ item.title }}</Link>
                                        </h4>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Sidebar (lg:col-span-1) -->
                    <aside class="lg:col-span-1 space-y-8">
                        
                         <!-- Latest Widget (Sidebar Version) -->
                         <div class="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700" v-if="latestServices && latestServices.length">
                             <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <i class="fa fa-bolt text-orange-500"></i> Terbaru
                             </h3>
                             <div class="space-y-4">
                                 <div v-for="item in latestServices" :key="item.id" class="flex gap-3 group">
                                     <div class="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                                         <Link :href="`/layanan/${item.slug}`">
                                             <img :src="getImageUrl(item)" class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" :alt="item.title">
                                         </Link>
                                     </div>
                                     <div class="flex-1">
                                         <h4 class="text-sm font-bold text-gray-900 dark:text-white leading-snug group-hover:text-orange-600 transition-colors line-clamp-2 mb-1">
                                             <Link :href="`/layanan/${item.slug}`">{{ item.title }}</Link>
                                         </h4>
                                         <span class="text-xs text-gray-400"><i class="fa fa-calendar mr-1"></i> {{ formatDate(item.date_taken || item.created_at, 'short') }}</span>
                                     </div>
                                 </div>
                             </div>
                        </div>

                        <!-- Archive Widget -->
                        <div class="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700" v-if="archives && archives.length">
                             <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <i class="fa fa-archive text-orange-500"></i> Arsip Layanan
                             </h3>
                             <div class="flex flex-col">
                                 <Link v-for="(archive, idx) in archives" :key="idx" :href="`/layanan?archive_year=${archive.year}&archive_month=${archive.month}`"
                                       class="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-0 hover:pl-2 transition-all duration-300 group">
                                     <div class="flex items-center gap-3">
                                         <i class="fa fa-calendar text-gray-400 group-hover:text-orange-500 transition-colors"></i>
                                         <span class="text-gray-600 dark:text-gray-300 font-medium text-sm group-hover:text-orange-600 transition-colors">{{ getMonthName(archive.month) }} {{ archive.year }}</span>
                                     </div>
                                     <span class="bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs py-1 px-2 rounded group-hover:bg-orange-100 dark:group-hover:bg-orange-900/30 group-hover:text-orange-600 transition-colors">{{ archive.count }}</span>
                                 </Link>
                             </div>
                        </div>

                        <!-- Info/Contact Widget (Optional filler) -->
                        <div class="bg-gradient-to-br from-orange-500 to-amber-500 dark:from-orange-600 dark:to-amber-600 rounded-2xl p-4 text-white text-center shadow-lg">
                            <h3 class="text-xl font-bold mb-2">Butuh Layanan Ini?</h3>
                            <p class="text-white/90 text-sm mb-6">Hubungi kami sekarang untuk mendapatkan penawaran terbaik.</p>
                            <a :href="'https://wa.me/' + (setting?.setting_no_wa || '')" target="_blank" class="inline-block bg-white text-orange-600 font-bold py-2.5 px-6 rounded-full hover:bg-orange-50 transition-colors shadow-sm">
                                <i class="fa fa-whatsapp mr-1"></i> Chat WhatsApp
                            </a>
                        </div>
                    </aside>
                </div>
            </div>
        </section>
    </FrontLayout>
</template>
